import { Link } from "react-router-dom";
import Section from "../../components/ui/Section";
import AirgDecisionBorder from "../../components/ui/AirgDecisionBorder";

const AIRG = () => {
  return (
    <div className="bg-bg">
      <section className="pt-12 pb-14 sm:pt-16 sm:pb-16 border-b border-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <Link to="/products" className="kicker hover:text-signal transition-colors">
              NOVTIA Products
            </Link>
            <span className="status-pill">Live with design partners</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-ink mb-5 leading-[1.06] max-w-4xl">
            AIRG — a runtime control plane for AI agents.
          </h1>
          <p className="text-sm sm:text-base text-muted max-w-2xl mb-7">
            AIRG intercepts every agent action — tool calls, API calls, model
            calls — and evaluates it against policy, risk, budget, and identity
            checks. It returns allow, review, or block before the action reaches
            a real system, not after.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-signal px-5 py-2.5 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
          >
            Discuss an AIRG pilot
          </Link>
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-[1.2fr,0.8fr] gap-8 items-start text-sm">
          <div className="space-y-3">
            <p className="text-ink">
              As organisations move from single prompts to agents that
              actually take actions, the question shifts from “can it do
              this?” to “should it — and how do we prove what happened if it
              did?” AIRG is built around that second question.
            </p>
            <p className="text-muted">
              It doesn&apos;t score a transcript after the fact. It sits in
              the execution path itself, as the thing an agent&apos;s tool
              call or model call has to pass through, and it keeps a signed
              record of every decision it makes.
            </p>
          </div>
          <div className="surface-card p-5">
            <h3 className="font-display text-base font-semibold text-ink mb-2">
              Where AIRG fits
            </h3>
            <ul className="space-y-1.5 text-muted">
              <li>• High-stakes tool calls — payments, infra, customer data.</li>
              <li>
                • Multi-step agents that need a real enforcement point, not
                just a scored log.
              </li>
              <li>• Teams that need audit-ready evidence, not screenshots.</li>
              <li>
                • Organisations that can&apos;t accept &quot;the agent did
                something and we&apos;re not sure why.&quot;
              </li>
            </ul>
          </div>
        </div>
      </Section>

      <section className="border-t border-line">
        <Section
          eyebrow="AIRG in operation"
          title="Every consequential action meets the Decision Border."
          description="AIRG sits between an agent and the systems it can affect. The control map below illustrates the policy, risk, identity, and evidence checks that return a verdict before execution."
        >
          <AirgDecisionBorder />
        </Section>
      </section>

      <div className="border-t border-line">
        <Section
          eyebrow="Architecture"
          title="Two gateways, one control plane."
          description="AIRG runs a dual gateway because a governance layer that only reads text misses the agent that quietly calls a delete-infrastructure tool. Both routes call the same policy engine, so one set of rules governs an agent regardless of the surface it acts through."
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div className="surface-card p-6">
              <p className="kicker mb-3">Gateway 01 · What agents say</p>
              <h3 className="font-display text-lg font-semibold text-ink mb-2">
                LLM gateway
              </h3>
              <p className="text-sm text-muted mb-4">
                Sits in front of model calls, evaluating prompts and responses
                before they move on.
              </p>
              <ul className="space-y-2 text-sm text-muted">
                <li className="flex gap-2.5">
                  <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                  <span>Injection and PII scanning on request and response.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                  <span>Scope limits on what a given agent may ask for.</span>
                </li>
              </ul>
            </div>

            <div className="surface-card p-6">
              <p className="kicker mb-3">Gateway 02 · What agents do</p>
              <h3 className="font-display text-lg font-semibold text-ink mb-2">
                Tool &amp; API gateway
              </h3>
              <p className="text-sm text-muted mb-4">
                Sits in front of real upstreams, evaluating each tool and API
                call before execution.
              </p>
              <ul className="space-y-2 text-sm text-muted">
                <li className="flex gap-2.5">
                  <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                  <span>Destructive-action thresholds defined by policy.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                  <span>Blocked and reviewed calls never reach the target.</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="surface-card mt-5 p-6 border-signal/40">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="max-w-xl">
                <p className="kicker mb-3">Shared policy engine</p>
                <p className="text-sm text-ink mb-3">
                  Both gateways resolve to one evaluation: identity, scope,
                  policy match, and budget — scored before the request reaches
                  a real upstream, with a signed receipt written either way.
                </p>
                <p className="text-sm text-muted">
                  Entry points: Python SDK · MCP server · direct gateway.
                </p>
              </div>
              <div className="flex gap-2 flex-none">
                <span className="verdict-chip verdict-chip--allow">ALLOW</span>
                <span className="verdict-chip">REVIEW</span>
                <span className="verdict-chip">BLOCK</span>
              </div>
            </div>
          </div>
        </Section>
      </div>

      <div className="border-t border-line">
        <Section eyebrow="Use cases" title="Where AIRG adds the most value.">
          <div className="surface-card grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-line">
            <div className="p-6">
              <h3 className="font-display text-base font-semibold text-ink mb-2">
                Financial &amp; payment agents
              </h3>
              <p className="text-sm text-muted mb-3">
                SURGE budget gating means a payment-transfer call costs more
                against budget than a read-only lookup — agents that exhaust
                it stop automatically.
              </p>
              <ul className="space-y-1 text-xs text-muted">
                <li>• Risk-priced governance credits per action.</li>
                <li>• Automatic halt on budget exhaustion.</li>
                <li>• Signed receipt on every transfer decision.</li>
              </ul>
            </div>

            <div className="p-6">
              <h3 className="font-display text-base font-semibold text-ink mb-2">
                Infrastructure &amp; DevOps agents
              </h3>
              <p className="text-sm text-muted mb-3">
                Destructive commands are blocked or routed to human review by
                policy — not by hoping the agent behaves.
              </p>
              <ul className="space-y-1 text-xs text-muted">
                <li>• Policy-defined destructive-action thresholds.</li>
                <li>• Human review path for production-impacting calls.</li>
                <li>• Full topology trace: actor, gateway, tool, target.</li>
              </ul>
            </div>

            <div className="p-6">
              <h3 className="font-display text-base font-semibold text-ink mb-2">
                Enterprise data agents
              </h3>
              <p className="text-sm text-muted mb-3">
                PII scanning and scope enforcement on every customer-data
                read, with the evidence to prove it during an audit.
              </p>
              <ul className="space-y-1 text-xs text-muted">
                <li>• PII and injection scanning on every request.</li>
                <li>• Scope-enforced access to customer records.</li>
                <li>• Compliance reports generated from real evaluation history.</li>
              </ul>
            </div>
          </div>
        </Section>
      </div>

      <div className="border-t border-line">
        <Section eyebrow="Integration" title="How AIRG fits into your stack.">
          <div className="surface-card grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-line">
            <div className="p-6 space-y-2">
              <h3 className="font-display text-base font-semibold text-ink mb-1">
                Sits in the request path, not beside it.
              </h3>
              <p className="text-sm text-muted">
                Three entry points ship today: the Python SDK, an MCP server
                connection, or a direct gateway call in front of your LLM or
                tool/API upstream.
              </p>
              <pre className="mt-3 bg-surface-2 border border-line p-3 text-[11px] text-ink overflow-x-auto">
{`pip install airg-client

from airg import AIRG
client = AIRG()
decision = client.evaluate(
    tool="payments.transfer",
    args={"amount": 25000},
)
print(decision["decision"])`}
              </pre>
            </div>
            <div className="p-6 space-y-2">
              <h3 className="font-display text-base font-semibold text-ink mb-1">
                Engagement model
              </h3>
              <p className="text-sm text-muted">
                AIRG is taking on a small number of design partners to govern
                real agent actions through live integrations.
              </p>
              <ul className="space-y-1 text-xs text-muted">
                <li>• Direct access to the team building AIRG.</li>
                <li>• Input on what ships next, based on how you&apos;re using it.</li>
                <li>• Governed-action pricing locked in at the early-access rate.</li>
              </ul>
            </div>
          </div>
        </Section>
      </div>

      <div className="border-t border-line">
        <Section>
          <div className="surface-card p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className="kicker mb-3">Explore a pilot</p>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-ink mb-2">
                See how AIRG governs your real agent traffic.
              </h2>
              <p className="text-sm text-muted max-w-xl">
                If you&apos;re running agents that touch real systems and
                need governance, evidence, and control you can stand behind,
                we&apos;d be glad to talk.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center bg-signal px-6 py-2.5 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors flex-none"
            >
              Discuss an AIRG pilot
            </Link>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default AIRG;
